import React, { useState, useMemo, useEffect } from 'react';
import { Button, Checkbox, FormControlLabel, FormGroup, withStyles, Typography } from '@material-ui/core';
import { Bar } from 'react-chartjs-2';
import 'chartjs-plugin-zoom';
import MaterialTable from 'material-table';
import Toolbar from '../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../themes';
import { connect } from 'react-redux';
import * as actions from './store/kpiActions';
import DataRange from '../../sc-utilities/DateRange';
import { localization } from '../../sc-utilities';
import * as action from '../../np-core/Auth/store/authActions';
import AppLogoLoading from "../../sc-logo";
import moment from "moment";
import momentTimezone from 'moment-timezone';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
        "[class*='.MuiButton-root']": {
            minWidth: '24px',
            borderRadius: '1px'
        },
    },
    title: {
        marginLeft: 14,
        width: "900px",
        paddingTop: '4px'
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
        paddingTop: '4px'
    },
    tableTitleRefresh: {
        marginBottom: '32px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    loading: {
        marginLeft: 'auto',
        marginRight: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    checkboxes: {
        display: 'flex'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const StatisticList = props => {
    const { activeCustomerID, fetchKPIOverview, translations, tableColumns, tableRef, classes, saveColumnsFilter, refetchTab, setRefetchTab } = props;
    const [data, setData] = useState([]);
    const [timeGroup, setTimeGroup] = useState('months');
    const [view, setView] = useState('processed');
    const [barChartdata, setBarChartData] = useState(null);
    const [GroupByUser, setGroupByUser] = useState(true);
    const [SuccessfulErasuresOnly, setSuccessfulErasuresOnly] = useState(false);

    const [loading, setLoading] = useState(false);

    const columnObject = useMemo(() => tableColumns, [tableColumns]);

    const [dateRangeValue, setDateRangeValue] = useState(null);

    const [tableOptions,] = useState({
        filtering: true,
        sorting: false,
        selection: false,
        search: false,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                <Typography className={classes.typography}>{translations[`kpi_overview`] || `Key Performance Indicator Overview`}</Typography>
            </div>
        </div>
    };

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`KPIOverview`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    };

    const fetchData = async () => {
        setLoading(true);
        let res = await fetchKPIOverview({ ActiveCustomerID: activeCustomerID, GroupByUser, SuccessfulErasuresOnly, From: dateRangeValue?.From.format('YYYY-MM-DD'), To: dateRangeValue?.To.format('YYYY-MM-DD') });
        setRefetchTab(false);
        if (res) {
            setData(res);
            setLoading(false);
            return { data: res, page: 0, pageSize: res.length, totalCount: res.length }
        }
        setLoading(false);
    }

    useEffect(() => {
        if (data.length > 0) {
            setBarChartData({
                labels: Object.keys(data.reduce((acc, curr) => ({ ...acc, ...curr.statistics[view][timeGroup] }), {})).sort(),
                datasets: Object.values(data).map(value => ({
                    label: `${value.FirstName} ${value.LastName}`,
                    backgroundColor: value.backgroundColor,
                    data: Object.values(value.statistics[view][timeGroup])
                }))
            })

        } else {
            setBarChartData({})
        }
    }, [data, timeGroup, view]);

    useEffect(() => {
        const customerTimezone = window.localStorage.getItem("CustomerTimezone") ?? 'UTC';

        const from = momentTimezone.tz(moment().subtract(7, 'days'), customerTimezone);

        const to = momentTimezone.tz(moment(), customerTimezone);
        
        setDateRangeValue({
            From: from,
            To: to,
            maxDate: to.set({
                hours: 23,
                minutes: 59,
                seconds: 59,
            })
        });
        // eslint-disable-next-line
    }, [ activeCustomerID ]);

    const setTimeGroupDays = () => {
        setTimeGroup('days');
    }
    const setTimeGroupWeeks = () => {
        setTimeGroup('weeks');
    }
    const setTimeGroupMonths = () => {
        setTimeGroup('months');
    }
    const setViewProcessed = () => {
        setView('processed');
    }
    const setViewHourly = () => {
        setView('hourly');
    }

    const handleChangeCheckbox = (setStateFunction) => {
        setStateFunction(prevState => !prevState);
    }

    const filterDataRange = range => {
        setDateRangeValue({ From: range.From, To: range.To });
    };

    useEffect(() => {
        if (refetchTab) {
            tableRef.current && tableRef.current.onQueryChange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchTab]);

    return (
        <div>
            <div>
                {dateRangeValue && <DataRange
                    initialValues={dateRangeValue}
                    filterDataRange={filterDataRange}
                    value={dateRangeValue}
                    setValue={setDateRangeValue}
                    format="yyyy/MM/dd 00:00:00"
                    translations={translations} 
                />}
            </div>

            <div className={classes.checkboxes}>
                <FormGroup>
                    <FormControlLabel control={<Checkbox />} checked={GroupByUser} label={translations["group_by_user"] || "Group by user"} onChange={() => handleChangeCheckbox(setGroupByUser)} />
                </FormGroup>
                <FormGroup>
                    <FormControlLabel control={<Checkbox />} checked={SuccessfulErasuresOnly} label={translations["successful_erasures_only"] || "Successful erasures only"} onChange={() => handleChangeCheckbox(setSuccessfulErasuresOnly)} />
                </FormGroup>
            </div>

            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchData}
                options={tableOptions}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />

            {barChartdata && <>
                <div>
                    <Button onClick={setViewProcessed}>{translations["absolute"] || "Absolute"}</Button>
                    <Button onClick={setViewHourly}>{translations["hourly"] || "Hourly"}</Button>
                </div>
                <div>
                    <Button onClick={setTimeGroupDays}>{translations["daily_view"] || "Daily view"}</Button>
                    <Button onClick={setTimeGroupWeeks}>{translations["weekly_view"] || "Weekly view"}</Button>
                    <Button onClick={setTimeGroupMonths}>{translations["monthly_view"] || "Monthly view"}</Button>

                    {loading ? <div className={classes.loading}><AppLogoLoading /></div> : <Bar data={barChartdata} redraw={false} options={{
                        zoom: {
                            enabled: true,
                            mode: 'x',
                        },
                        pan: {
                            enabled: true,
                            mode: 'x'
                        }
                    }} />}
                </div>
            </>}
        </div>
    )
}

const mapDispatchToProps = dispatch => {
    return {
        fetchKPIOverview: (params) => dispatch(actions.fetchKPIOverview(params)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data))
    };
};

const mapStateToProps = state => {
    return {
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID || null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        statistics: state.statistic.statistics
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(StatisticList));
